import React, { useCallback, useMemo } from 'react'

import { useReactFlow } from '@xyflow/react'

import Box from 'components_new/atoms/Box'

import Node, { InputType } from 'components_new/molecules/Node'
import FilterContainer from 'components_new/molecules/Node/Filter'
import { FieldOption } from 'components_new/molecules/Node/FieldSelect'

import NodeType from 'types/transformation/enums/NodeType'

import {
  FilterCondition,
  FilterNode,
  Node as TNode
} from 'types/transformation/types/nodes'

import { pruneRef, useInputSchema, useIsInvalid, usePruneRefs, useColumnOrigins } from '../hooks'

interface FilterNodeProps {
  id: string
  data: FilterNode
  selected: boolean
}

const Filter = (props: FilterNodeProps) => {
  const { id, data, selected } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchema = useInputSchema(data.left_node)

  const originByNode = useColumnOrigins()

  const fieldOptions = useMemo(
    (): FieldOption[] =>
      inputSchema.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [inputSchema, originByNode]
  )

  // input schema has changed, validate references
  usePruneRefs<FilterNode>(
    id,
    (nodeData) => ({
      ...nodeData,
      filter_conditions: nodeData.filter_conditions.map((fc) => ({
        ...fc,
        left_column: pruneRef(fc.left_column, inputSchema),
        right_column: pruneRef(fc.right_column, inputSchema)
      }))
    }),
    [inputSchema]
  )

  const handleChangeFilterConditions = useCallback(
    (nextConditions: FilterCondition[]) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          return {
            ...node,
            data: {
              ...node.data,
              filter_conditions: nextConditions.map((condition, index) => ({
                ...condition,
                index
              }))
            }
          }
        })
      )
    },
    [setNodes, id]
  )

  return (
    <Node
      error={isInvalid}
      icon="Filter"
      inputType={InputType.SINGLE}
      title={'Filter'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.FILTER}
    >
      <Box sx={{ width: '360px' }}>
        <FilterContainer
          value={data.filter_conditions}
          onChange={handleChangeFilterConditions}
          columns={fieldOptions}
        />
      </Box>
    </Node>
  )
}

export default Filter
